/**
 * Aggregation Utils
 * 데이터 집계/합계 계산 유틸리티
 *
 * @description
 * 테이블 데이터의 합계, 평균 등 집계 계산에 사용되는 공통 함수
 * - 배열 합계 계산
 * - 다중 키 합계 동시 계산
 * - 평균/비율 계산
 */

// ============================================================
// 타입 정의
// ============================================================

/**
 * 숫자 값을 가지는 키만 추출하는 타입
 */
type NumericKeys<T> = {
    [K in keyof T]: T[K] extends number | undefined ? K : never;
}[keyof T];

/**
 * 합계 결과 타입
 */
type SumResult<T, K extends keyof T> = {
    [P in K]: number;
};

// ============================================================
// 단일 키 합계 함수
// ============================================================

/**
 * 배열에서 특정 키의 합계 계산
 *
 * @template T 데이터 타입
 * @param data 데이터 배열
 * @param key 합계를 구할 키
 * @returns 합계 값
 *
 * @example
 * const total = sumByKey(items, 'amount');
 */
export const sumByKey = <T>(data: T[], key: keyof T): number => {
    if (!data || data.length === 0) return 0;

    return data.reduce((sum, item) => {
        const value = item[key];
        return sum + (typeof value === 'number' ? value : 0);
    }, 0);
};

// ============================================================
// 다중 키 합계 함수
// ============================================================

/**
 * 배열에서 여러 키의 합계를 한 번에 계산 (최적화된 단일 순회)
 *
 * @template T 데이터 타입
 * @template K 합계를 구할 키들
 * @param data 데이터 배열
 * @param keys 합계를 구할 키 배열
 * @returns 키별 합계 객체
 *
 * @example
 * const totals = sumByKeys(items, ['finishCnt', 'rejectCnt', 'cancelCnt']);
 * // { finishCnt: 100, rejectCnt: 5, cancelCnt: 3 }
 */
export const sumByKeys = <T, K extends keyof T>(
    data: T[],
    keys: K[]
): SumResult<T, K> => {
    // 초기값 생성
    const initial = keys.reduce((acc, key) => {
        acc[key] = 0;
        return acc;
    }, {} as SumResult<T, K>);

    if (!data || data.length === 0) return initial;

    // 단일 순회로 모든 합계 계산
    return data.reduce((acc, item) => {
        for (const key of keys) {
            const value = item[key];
            if (typeof value === 'number') {
                acc[key] += value;
            }
        }
        return acc;
    }, initial);
};

// ============================================================
// 배달 통계 전용 합계 함수
// ============================================================

/**
 * 배달 건수 통계 합계 결과 타입
 */
export interface DeliveryCountTotals {
    finishCnt: number;
    rejectCnt: number;
    assingCancelCnt: number;
    deliCancelCnt: number;
    morTimeCnt: number;
    afterNonCnt: number;
    nightCnt: number;
    nightNonCnt: number;
}

/**
 * 배달 통계용 합계 키 목록
 */
const DELIVERY_COUNT_KEYS: (keyof DeliveryCountTotals)[] = [
    'finishCnt',
    'rejectCnt',
    'assingCancelCnt',
    'deliCancelCnt',
    'morTimeCnt',
    'afterNonCnt',
    'nightCnt',
    'nightNonCnt',
];

/**
 * 배달 건수 통계 초기값
 */
export const EMPTY_DELIVERY_COUNT_TOTALS: DeliveryCountTotals = {
    finishCnt: 0,
    rejectCnt: 0,
    assingCancelCnt: 0,
    deliCancelCnt: 0,
    morTimeCnt: 0,
    afterNonCnt: 0,
    nightCnt: 0,
    nightNonCnt: 0,
};

/**
 * 배달 건수 통계 합계 계산 (단일 순회 최적화)
 *
 * @template T DeliveryCountTotals 키를 포함하는 타입
 * @param data 데이터 배열
 * @returns 배달 건수 합계
 *
 * @example
 * const totals = calculateDeliveryCountTotals(deliveryHistory);
 */
export const calculateDeliveryCountTotals = <T extends Partial<DeliveryCountTotals>>(
    data: T[]
): DeliveryCountTotals => {
    if (!data || data.length === 0) {
        return { ...EMPTY_DELIVERY_COUNT_TOTALS };
    }

    return data.reduce(
        (acc, item) => {
            acc.finishCnt += item.finishCnt || 0;
            acc.rejectCnt += item.rejectCnt || 0;
            acc.assingCancelCnt += item.assingCancelCnt || 0;
            acc.deliCancelCnt += item.deliCancelCnt || 0;
            acc.morTimeCnt += item.morTimeCnt || 0;
            acc.afterNonCnt += item.afterNonCnt || 0;
            acc.nightCnt += item.nightCnt || 0;
            acc.nightNonCnt += item.nightNonCnt || 0;
            return acc;
        },
        { ...EMPTY_DELIVERY_COUNT_TOTALS }
    );
};

// ============================================================
// 비율/평균 계산 함수
// ============================================================

/**
 * 수행률 계산 (완료 건수 / 총 건수 * 100)
 *
 * @param completed 완료 건수
 * @param total 총 건수
 * @param decimals 소수점 자릿수 (기본: 1)
 * @returns 수행률 (%)
 *
 * @example
 * calculatePerformanceRate(80, 100) // 80.0
 * calculatePerformanceRate(85, 100, 2) // 85.00
 */
export const calculatePerformanceRate = (
    completed: number,
    total: number,
    decimals = 1
): number => {
    if (total === 0) return 0;
    return Number(((completed / total) * 100).toFixed(decimals));
};

/**
 * 배열 평균 계산
 *
 * @template T 데이터 타입
 * @param data 데이터 배열
 * @param key 평균을 구할 키
 * @param decimals 소수점 자릿수 (기본: 1)
 * @returns 평균값
 */
export const averageByKey = <T>(
    data: T[],
    key: keyof T,
    decimals = 1
): number => {
    if (!data || data.length === 0) return 0;

    const sum = sumByKey(data, key);
    return Number((sum / data.length).toFixed(decimals));
};
